import { loadStripe, Stripe, StripeElements, StripeElementsOptions, Appearance, StripePaymentElement  } from '@stripe/stripe-js';
import bookingDataStorage from '../data-storage';

// Posthog
import ph from '../../../analytics/posthog/posthog-client';

import googleAdv from '../../../analytics/google-adv';

declare let core_object: any;
declare let booking_object: any;

// Interface for Booking Data
interface BookingData {
    order_uuid: string;
    room_id: number;
    room_name: string;
    game_name: string;
    arena_id: number;
    type_arena: string;
    mode: string;
    game_id: number;
    date_start: string;
    date_end: string;
    players: number;
    price_paid: string;
    price_left: string;
    price_total: string;
    time: string;
    slot_time: string;
    day: string;
    name: string;
    surname: string;
    email: string;
    phone_prefix_id: number;
    phone: string;
    phone_full: string;
}

export default class BookingStripeProcess{
    
    private stripe: Stripe | null = null;
    private elements: StripeElements | null = null;
    private paymentIntentId: string | null = null;
    private modBookingDataStorage = new bookingDataStorage();
    private urlApiInit: string;
    private urlApiPaymentInit: string;
    private googleAdv = new googleAdv();
    
    constructor(
        private elForm: string,
        private elFormProcess: string,
        private elPaymentElement: string,
        private elLoadingPayment: string,
        private elPaymentResponse: string,
        private elPayReinit: string,
        private elFormBtn: string,
        private elSpinner: string,
        private elBtnText: string,
    ){
        
        this.urlApiInit = booking_object.rest_urls.init;
        this.urlApiPaymentInit = booking_object.rest_urls.payment_init;

        const urlParams = new URLSearchParams(window.location.search);

        // Verifica se redirect_status esiste nell'URL
        if(urlParams.has("redirect_status")){
            
            const redirectStatus = urlParams.get("redirect_status");

            // Se il pagamento è completato, nasconde il bottone
            if(redirectStatus === "succeeded"){
                
                const submitBtn = document.querySelector(this.elFormBtn) as HTMLButtonElement;
                if(submitBtn){
                    
                    submitBtn.disabled = true;
                    submitBtn.classList.add("hidden");

                }
                
            }
        }

    }

    /**
     * Initialize Stripe and elements
     */
    public async init(): Promise<void> {

        try{
            
            // Verifica se il form di pagamento esiste nel DOM
            const paymentForm = document.querySelector(this.elForm) as HTMLFormElement | null;

            if(!paymentForm){
                
                return;
                
            }

            const typeStripe = core_object.ph_key.type;
            const phKey = typeStripe === 'test' ? core_object.ph_key.test : core_object.ph_key.live;
            this.stripe = await loadStripe(phKey);

        }catch(error){

            // console.error('Error loading Stripe:', error);

        }

    }

    /**
     * Show the payment form
     */
    public async showForm(): Promise<void> {

        const clientSecretParam = new URLSearchParams(window.location.search).get("payment_intent_client_secret");
        
        this.setProcessing(true);

        if(!clientSecretParam){
            
            this.setProcessing(false);
            await this.initialize();

        }

        await this.checkStatus();
        this.handleSubmit();

    }

    /**
     * Handle processing state
     * @param isProcessing - Whether to show the processing state
     */
    private setProcessing(isProcessing: boolean): void {
        
        const elProcess = document.querySelector(this.elFormProcess) as HTMLElement | null;
        const paymentForm = document.querySelector(this.elForm) as HTMLFormElement | null;

        if(isProcessing){
            
            if(paymentForm) paymentForm.classList.add("hidden");
            if(elProcess) elProcess.classList.remove("hidden");
            
        }else{
            
            if(paymentForm) paymentForm.classList.remove("hidden");
            if(elProcess) elProcess.classList.add("hidden");
            
        }

    }

    /**
     * Initialize Stripe elements
     */
    private async initialize(): Promise<void> {
        
        try{

            // Verifica se il form di pagamento esiste nel DOM
            const paymentForm = document.querySelector(this.elForm) as HTMLFormElement | null;

            if(!paymentForm){
                
                return;

            }
            
            const response = await fetch(this.urlApiPaymentInit, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ request_type: 'create_payment_intent' }),
            });

            const { id, clientSecret } = await response.json();
            this.paymentIntentId = id;

            if(this.stripe && clientSecret){
                
                // Definiamo l'aspetto del form
                const appearance: Appearance = {
                    theme: "night", // Accettato: "night" | "stripe" | "flat"
                    labels: "floating"
                };
    
                // Configuriamo Stripe Elements con il clientSecret e appearance
                this.elements = this.stripe.elements({ clientSecret, appearance });
    
                // Opzioni per il Payment Element
                const paymentElementOptions: any = {
                    layout: "accordion",
                    defaultCollapsed: false
                };

                 // Verifica se l'elemento di pagamento esiste nel DOM
                const paymentElementContainer = document.querySelector(this.elPaymentElement);

                if(!paymentElementContainer){
                    
                    return;

                }
    
                // Creiamo l'elemento di pagamento forzando la tipizzazione corretta
                const paymentElement = this.elements.create("payment" as any, paymentElementOptions);
    
                // Montiamo l'elemento nel DOM
                paymentElement.mount(this.elPaymentElement);
    
                // Nascondiamo il loader quando l'elemento è pronto
                paymentElement.on('ready', () => {
                    document.querySelector(this.elLoadingPayment)?.classList.add('hidden');
                });

            }

        }catch(error){
            
            // console.error('Error initializing payment:', error);

        }

    }

    /**
     * Check Payment Intent status
     */
    private async checkStatus(): Promise<void> {
        
        const clientSecret = new URLSearchParams(window.location.search).get("payment_intent_client_secret");
        const customerID = new URLSearchParams(window.location.search).get("customer_id");

        if(!clientSecret || !this.stripe) return;

        try{
            const { paymentIntent } = await this.stripe.retrievePaymentIntent(clientSecret);
            
            if(!paymentIntent){
                
                this.showMessage(booking_object.payment_error_messages.general);
                this.setReinit();
                return;

            }

            switch(paymentIntent.status){
                
                case "succeeded":
                    
                    await this.processPaymentSuccess(paymentIntent, customerID);

                break;
                
                case "processing":
                    
                    this.showMessage(booking_object.payment_error_messages.processing);
                    this.setReinit();

                break;
                
                case "requires_payment_method":
                    
                    this.showMessage(booking_object.payment_error_messages.payment_failed);
                    this.setReinit();

                break;
                
                default:
                    
                    this.showMessage(booking_object.payment_error_messages.general);
                    this.setReinit();

                break;
                
            }

        }catch(error){
            
            // console.error('Error checking payment status:', error);

        }

    }

    /**
     * Process successful payment and send data to the server
     * @param paymentIntent - The Stripe payment intent object
     * @param customerID - The customer ID
     */
    private async processPaymentSuccess(paymentIntent: any, customerID: string | null): Promise<void> {
    
        try{
            
            // Retrieve stored booking data
            const storedValues = this.modBookingDataStorage.getLocalStorage();

            const response = await fetch(this.urlApiPaymentInit, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    request_type: 'payment_insert',
                    payment_intent: paymentIntent,
                    customer_id: customerID,
                    order_uuid: storedValues?.order_uuid,
                    room_id: storedValues?.room_id,
                    room_name: storedValues?.room_name,
                    game_name: storedValues?.game_name,
                    arena_id: storedValues?.arena_id,
                    type_arena: storedValues?.type_arena,
                    mode: storedValues?.mode,
                    game_id: storedValues?.game_id,
                    date_start: storedValues?.date_start,
                    date_end: storedValues?.date_end,
                    players: storedValues?.players,
                    price_paid: storedValues?.price_paid,
                    price_left: storedValues?.price_left,
                    price_total: storedValues?.price_total,
                    time: storedValues?.time,
                    slot_time: storedValues?.slot_time,
                    day: storedValues?.day,
                    phone_prefix_id: storedValues?.phone_prefix_id,
                    phone: storedValues?.phone,
                    customer: {
                        name: storedValues?.name,
                        surname: storedValues?.surname,
                        email: storedValues?.email,
                        phone: storedValues?.phone_full,
                    },
                }),
            });

            const data = await response.json();

            if(data.payment_txn_id !== 0){
                
                // Send analytics
                const posthogData = this.collectPosthogData(storedValues);
                ph.captureBooking('Booking', posthogData.name, posthogData.surname, posthogData.email, posthogData.phone, posthogData.price);

                // Send Google adv
                this.googleAdv.captureGtmEvent(storedValues?.room_id, storedValues?.price_paid, storedValues?.order_uuid);
                
                // Remove stored data after successful payment
                this.modBookingDataStorage.removeStoredValue();
                this.modBookingDataStorage.removeStorageStep();

                // Redirect to the confirmation page
                const urlRedirect = `${booking_object.page_complete}?pid=${data.payment_txn_id}`;

                setTimeout(() => {
                    window.location.replace(urlRedirect);
                }, 2000);

            }else{
            
                this.showMessage(data.error);
                this.setReinit();

            }
        
        }catch(error){
            
            this.showMessage(booking_object.payment_error_messages.general);
            this.setReinit();

        }

    }

    /**
     * Handle form submission
     */
    private handleSubmit(): void {

        const btn = document.querySelector(this.elFormBtn) as HTMLButtonElement;

        btn?.addEventListener("click", async (e) => {
            e.preventDefault();
            this.setLoading(true);

            try{
                const storedValues = this.modBookingDataStorage.getLocalStorage();

                const response = await fetch(this.urlApiPaymentInit, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        request_type: 'create_customer',
                        payment_intent_id: this.paymentIntentId,
                        amount: storedValues?.price_paid,
                        customer: {
                            name: storedValues?.name,
                            surname: storedValues?.surname,
                            email: storedValues?.email,
                            phone: storedValues?.phone_full,
                        },
                    }),
                });

                const { customer_id } = await response.json();

                if(this.stripe && this.elements){
                    
                    const { error } = await this.stripe.confirmPayment({
                        elements: this.elements,
                        confirmParams: { return_url: `${window.location.href}?customer_id=${customer_id}` },
                    });

                    if(error){
                        this.showMessage(error.message || "An unexpected error occurred.");
                    }

                }

            }catch(error){
                
                // console.error('Error handling payment submission:', error);

            }finally{
                
                this.setLoading(false);

            }

        });

    }

    /**
     * Show a message to the user
     * @param messageText - The message to display
     */
    private showMessage(messageText: string): void {
        
        const messageContainer = document.querySelector(this.elPaymentResponse);

        if(messageContainer){
            
            messageContainer.textContent = messageText;
            messageContainer.classList.remove("hidden");

            setTimeout(() => {
                messageContainer.classList.add("hidden");
                messageContainer.textContent = "";
            }, 5000);

        }

    }

    /**
     * Handle loading state
     * @param isLoading - Whether the form is loading
     */
    private setLoading(isLoading: boolean): void{
        
        const submitBtn = document.querySelector(this.elFormBtn) as HTMLButtonElement;
        const spinner = document.querySelector(this.elSpinner);
        const buttonText = document.querySelector(this.elBtnText);

        submitBtn.disabled = isLoading;
        spinner?.classList.toggle("hidden", !isLoading);
        buttonText?.classList.toggle("hidden", isLoading);

    }

    /**
     * Show payment re-initiate button
     */
    private setReinit(): void{
        
        document.querySelector(this.elForm)?.classList.add("hidden");
        document.querySelector(this.elFormBtn)?.classList.add("hidden");
        document.querySelector(this.elFormProcess)?.classList.add("hidden");
        document.querySelector(this.elPayReinit)?.classList.remove("hidden");

    }

    /**
     * Collects user input data for analytics.
     */
    private collectPosthogData(storedValues: BookingData | null){
        
        return {
            name: storedValues?.name,
            surname: storedValues?.surname,
            email: storedValues?.email,
            phone: storedValues?.phone_full,
            price: storedValues?.price_paid
        };

    }

}
